import { useToast } from "@chakra-ui/react";
import { useState } from "react";

import { Tag, TagDictionary } from "@bcasmart/doxecure-commons/doxecureTypes";
import { $ok } from "foundation-ts/commons";
import { TSError } from "foundation-ts/tserrors";
import { Nullable } from "foundation-ts/types";

import { useAuthContext } from "@hooks/useAuthContext";
import { updateUserTags } from "@services/users.service";
import { defaultErrorToast } from "@/utils/toast";

export const useTagDictionary = () => {
    const { connectedUser, refreshConnectedUser } = useAuthContext();
    const [isCreating, setIsCreating] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const toast = useToast();
 
    const tags = connectedUser?.tags ?? {};
 
    const createTag = async (name: string, color?: string): Promise<Nullable<{ key: string, value: Tag }>> => {
        const trimmedName = name.trim();
        if (trimmedName.length === 0) { TSError.throw("Tag name cannot be empty"); }

        setIsCreating(true);
        try {
            const key = generateUniqueKey(trimmedName, tags);
            const newTag: Tag = { name: trimmedName.firstCap(), color: color ?? pickNextTagColor(Object.keys(tags).length), contactCount: 0 };
            const updatedTags: TagDictionary = { ...tags, [key]: newTag };
            const success = await updateUserTags(connectedUser!.apid!, updatedTags);
            if (!success) {
                toast(defaultErrorToast(`Immpossible de créer la catégorie ${name.trim().capitalize()}`));
                return undefined;
            }
            await refreshConnectedUser();
            return { key, value: newTag };
        } finally {
            setIsCreating(false);
        }
    }

    const deleteTags = async (keys: string[]): Promise<boolean> => {
        setIsDeleting(true);
        try {
            const remainingTags = { ...tags };
            keys.forEach(k => delete remainingTags[k]);
            const success = await updateUserTags(connectedUser!.apid!, remainingTags);
            if (!success) {
                toast(defaultErrorToast(`Impossible de supprimer ${keys.length > 1 ? "les catégories sélectionnées" : "la catégorie"}`));
                return false;
            }
            await refreshConnectedUser();
            return true;
        } finally {
            setIsDeleting(false);
        }
    }

    return { tags, isCreating, isDeleting, createTag, deleteTags };
}

const slugify = (name: string): string =>
    name.trim().toLowerCase()
        .normalize("NFD").replace(/[\u0300-\u036f]/g, "") // accents
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "");

const generateUniqueKey = (name: string, existing: TagDictionary): string => {
    const base = slugify(name) || "tag";
    let key = base;
    let i = 2;
    while ($ok(existing[key])) {
        key = `${base}-${i}`;
        i++;
    }
    return key;
};

const TAG_COLOR_PALETTE: string[] = [
    "#E53E3E", // red
    "#C53030", // red dark
    "#DD6B20", // orange
    "#C05621", // orange dark
    "#D69E2E", // yellow
    "#B7791F", // yellow dark
    "#38A169", // green
    "#2F855A", // green dark
    "#319795", // teal
    "#2C7A7B", // teal dark
    "#00A3C4", // cyan
    "#0987A0", // cyan dark
    "#3182CE", // blue
    "#2B6CB0", // blue dark
    "#2C5282", // blue darker
    "#5A67D8", // indigo
    "#4C51BF", // indigo dark
    "#805AD5", // purple
    "#6B46C1", // purple dark
    "#B83280", // pink dark
    "#D53F8C", // pink
    "#97266D", // pink darker
    "#718096", // gray
    "#4A5568", // gray dark
    "#DD6B90", // rose
    "#9C4221", // brown/orange dark
    "#276749", // forest green
    "#1A365D", // navy
    "#553C9A", // violet dark
    "#702459", // magenta dark
];
const pickNextTagColor = (existingCount: number): string => TAG_COLOR_PALETTE[existingCount % TAG_COLOR_PALETTE.length];